import { useEffect, useRef, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { getActivePinCode } from '../../api/pin';
import { pinsMatch } from '../../utils/closeApp';

const PIN_LENGTH = 6;
const emptyPin = () => Array(PIN_LENGTH).fill('');

function OTPModal({ show, onHide, onSuccess }) {
  const inputRefs = useRef([]);
  const [digits, setDigits] = useState(emptyPin);
  const [correctPin, setCorrectPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!show) {
      setDigits(emptyPin());
      setCorrectPin('');
      setError('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setDigits(emptyPin());
    setError('');

    getActivePinCode()
      .then((result) => {
        if (cancelled) return;
        if (result.success) setCorrectPin(result.pinCode);
        else setError(result.error || 'Failed to load PIN code.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        window.setTimeout(() => inputRefs.current[0]?.focus(), 100);
      });

    return () => {
      cancelled = true;
    };
  }, [show]);

  useEffect(() => {
    const threshold = 150;
    const initialHeight = window.innerHeight;
    const onResize = () => {
      setKeyboardOpen(initialHeight - window.innerHeight > threshold);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const close = () => {
    setDigits(emptyPin());
    setError('');
    onHide();
  };

  const validate = (nextDigits) => {
    if (!correctPin) {
      setError('PIN code is not ready. Please wait.');
      return;
    }
    if (pinsMatch(nextDigits.join(''), correctPin)) {
      close();
      onSuccess();
      return;
    }
    setDigits(emptyPin());
    setError('Incorrect PIN. Please try again.');
    inputRefs.current[0]?.focus();
  };

  const handleChange = (event, index) => {
    const value = event.target.value;
    if (!/^\d*$/.test(value)) return;

    const next = [...digits];
    next[index] = value.slice(-1);
    setDigits(next);
    setError('');

    if (value && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    if (next.every(Boolean)) validate(next);
  };

  return (
    <Modal
      show={show}
      onHide={close}
      backdrop="static"
      className={`otpModal${keyboardOpen ? ' keyboard-open' : ''}`}
      keyboard={false}
    >
      <Modal.Body>
        <div className="otpModalBody">
          <button type="button" className="otpModal__back" onClick={close} aria-label="Close PIN dialog">
            <svg width="50" height="50" viewBox="0 0 50 50" fill="none" aria-hidden="true">
              <rect width="50" height="50" rx="25" transform="matrix(-1 0 0 1 50 0)" fill="#FAFAFA" />
              <path
                d="M17 25L16.2929 24.2929L15.5858 25L16.2929 25.7071L17 25ZM32 26C32.5523 26 33 25.5523 33 25C33 24.4477 32.5523 24 32 24V25V26ZM23 19L22.2929 18.2929L16.2929 24.2929L17 25L17.7071 25.7071L23.7071 19.7071L23 19ZM17 25L16.2929 25.7071L22.2929 31.7071L23 31L23.7071 30.2929L17.7071 24.2929L17 25ZM17 25V26H32V25V24H17V25Z"
                fill="#33363F"
              />
            </svg>
          </button>
          <div className="otpModal__title">Enter your PIN code to access Admin Mode</div>

          {loading ? (
            <div className="otpModal__status">Loading PIN code…</div>
          ) : (
            <>
              <div className="otpModal__inputs">
                {digits.map((digit, index) => (
                  <Form.Control
                    key={index}
                    ref={(node) => {
                      inputRefs.current[index] = node;
                    }}
                    value={digit}
                    onChange={(event) => handleChange(event, index)}
                    onKeyDown={(event) => {
                      if (event.key === 'Backspace' && !digits[index] && index > 0) {
                        inputRefs.current[index - 1]?.focus();
                      }
                    }}
                    maxLength={1}
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    disabled={!correctPin}
                    autoComplete="off"
                    aria-label={`PIN digit ${index + 1}`}
                  />
                ))}
              </div>
              {error ? <div className="otpModal__error">{error}</div> : null}
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default OTPModal;
